import { useMemo, useState } from 'react';
import type {
  LocationColumnMap,
  LocationMatchConfig,
  LocationMatchPlatform,
  LocationMatchSheet,
  LocationMatchStatus,
  LocationMatchSummary,
} from '@tcg/shared';
import { ApiClientError, locationMatchApi } from '../lib/api';
import { Badge, Banner, Button, Card, CardBody, CardHeader, Field, Label, PageHeader, Spinner } from '../components/ui';
import { IconMatch } from '../components/icons';

/**
 * Location Match tool — reconcile a platform location list (Corrigo "CP" or
 * ServiceChannel "SC") against the internal FIT master list. The user uploads one
 * workbook, picks which sheet is which, maps the id/address/city/state/zip
 * columns (no hardcoded Excel letters), and downloads the MATCHED workbook.
 */

type ColField = keyof LocationColumnMap;

const COL_FIELDS: Array<{ key: ColField; label: string; required: boolean }> = [
  { key: 'id', label: 'ID', required: true },
  { key: 'address', label: 'Address', required: true },
  { key: 'city', label: 'City', required: false },
  { key: 'state', label: 'State', required: false },
  { key: 'zip', label: 'Zip', required: false },
];

const emptyMap: LocationColumnMap = { id: '', address: '', city: '', state: '', zip: '' };

/** Best-effort auto-pick a column whose header looks like the given field. */
function guessColumn(headers: string[], field: ColField): string {
  const patterns: Record<ColField, RegExp> = {
    id: /\b(id|number|store|site|location\s*id)\b/i,
    address: /\b(address|street|addr)\b/i,
    city: /\bcity\b/i,
    state: /\b(state|province|st)\b/i,
    zip: /\b(zip|postal)\b/i,
  };
  return headers.find((h) => patterns[field].test(h)) ?? '';
}

function autoMap(headers: string[]): LocationColumnMap {
  const m: LocationColumnMap = { ...emptyMap };
  for (const f of COL_FIELDS) m[f.key] = guessColumn(headers, f.key);
  return m;
}

function ColumnMapper({
  title,
  sheet,
  value,
  onChange,
}: {
  title: string;
  sheet: LocationMatchSheet | undefined;
  value: LocationColumnMap;
  onChange: (next: LocationColumnMap) => void;
}) {
  const headers = sheet?.headers ?? [];
  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <p className="mb-3 text-sm font-semibold text-slate-700">{title}</p>
      {!sheet ? (
        <p className="text-sm text-slate-400">Pick a sheet first.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {COL_FIELDS.map((f) => (
            <div key={f.key}>
              <Label>
                {f.label}
                {f.required && <span className="ml-0.5 text-red-500">*</span>}
              </Label>
              <select
                value={value[f.key] ?? ''}
                onChange={(e) => onChange({ ...value, [f.key]: e.target.value })}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="">{f.required ? '— choose —' : '(none)'}</option>
                {headers.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const STATUS_TONE: Record<LocationMatchStatus, 'green' | 'amber' | 'slate' | 'red'> = {
  Match: 'green',
  Close: 'amber',
  Mismatch: 'red',
  'No Reliable Match': 'slate',
};

export function LocationMatch() {
  const [platform, setPlatform] = useState<LocationMatchPlatform>('cp');
  const [file, setFile] = useState<File | null>(null);
  const [sheets, setSheets] = useState<LocationMatchSheet[] | null>(null);
  const [fitSheet, setFitSheet] = useState('');
  const [platformSheet, setPlatformSheet] = useState('');
  const [fitMap, setFitMap] = useState<LocationColumnMap>(emptyMap);
  const [platformMap, setPlatformMap] = useState<LocationColumnMap>(emptyMap);
  const [busy, setBusy] = useState<'inspect' | 'run' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<LocationMatchSummary | null>(null);

  const sheetByName = useMemo(() => {
    const m = new Map<string, LocationMatchSheet>();
    for (const s of sheets ?? []) m.set(s.name, s);
    return m;
  }, [sheets]);

  const reset = () => {
    setSheets(null);
    setFitSheet('');
    setPlatformSheet('');
    setFitMap(emptyMap);
    setPlatformMap(emptyMap);
    setSummary(null);
  };

  const onPickFile = (f: File | null) => {
    setFile(f);
    setError(null);
    reset();
  };

  const inspect = async () => {
    if (!file) return;
    setBusy('inspect');
    setError(null);
    setSummary(null);
    try {
      const res = await locationMatchApi.inspect(file);
      setSheets(res.sheets);
      // Pre-select FIT and the platform sheet by name when present.
      const fit = res.sheets.find((s) => /fit/i.test(s.name));
      const plat = res.sheets.find((s) => new RegExp(platform === 'cp' ? 'cp' : 'sc', 'i').test(s.name));
      if (fit) {
        setFitSheet(fit.name);
        setFitMap(autoMap(fit.headers));
      }
      if (plat && plat.name !== fit?.name) {
        setPlatformSheet(plat.name);
        setPlatformMap(autoMap(plat.headers));
      }
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not read that workbook.');
    } finally {
      setBusy(null);
    }
  };

  const onFitSheet = (name: string) => {
    setFitSheet(name);
    const s = sheetByName.get(name);
    setFitMap(s ? autoMap(s.headers) : emptyMap);
  };
  const onPlatformSheet = (name: string) => {
    setPlatformSheet(name);
    const s = sheetByName.get(name);
    setPlatformMap(s ? autoMap(s.headers) : emptyMap);
  };

  const canRun =
    file &&
    fitSheet &&
    platformSheet &&
    fitSheet !== platformSheet &&
    fitMap.id &&
    fitMap.address &&
    platformMap.id &&
    platformMap.address &&
    busy === null;

  const run = async () => {
    if (!file) return;
    const config: LocationMatchConfig = {
      platform,
      fitSheet,
      platformSheet,
      fit: fitMap,
      platformCols: platformMap,
    };
    setBusy('run');
    setError(null);
    try {
      setSummary(await locationMatchApi.run(file, config));
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'The match run failed.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/"
        icon={<IconMatch className="h-5 w-5" />}
        title="Location Match"
        subtitle={
          <>
            Reconcile a platform location list against the FIT master list. Upload one workbook with a
            FIT sheet and a platform sheet, map the columns, and download a MATCHED workbook with a Key
            sheet (platform&nbsp;ID&nbsp;→&nbsp;FIT&nbsp;ID).
          </>
        }
      />

      {error && <Banner tone="error">{error}</Banner>}

      {/* 1. Platform + file */}
      <Card>
        <CardHeader title="1. Workbook" subtitle="One .xlsx with a FIT sheet and a platform sheet." />
        <CardBody className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <Label>Platform</Label>
              <div className="flex gap-2">
                {(['cp', 'sc'] as const).map((p) => (
                  <Button
                    key={p}
                    variant={platform === p ? 'primary' : 'secondary'}
                    onClick={() => setPlatform(p)}
                  >
                    {p === 'cp' ? 'Corrigo (CP)' : 'ServiceChannel (SC)'}
                  </Button>
                ))}
              </div>
            </div>
            <div className="grow">
              <Label>Workbook (.xlsx)</Label>
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium hover:file:bg-slate-200"
              />
            </div>
            <Button onClick={() => void inspect()} disabled={!file || busy !== null}>
              {busy === 'inspect' ? <Spinner /> : 'Read Sheets'}
            </Button>
          </div>
          {platform === 'cp' && (
            <p className="text-xs text-slate-400">
              CP runs a smart dedup first — one best row per id before matching.
            </p>
          )}
        </CardBody>
      </Card>

      {/* 2. Sheet + column mapping */}
      {sheets && (
        <Card>
          <CardHeader title="2. Map columns" subtitle="Choose which sheet is which, then map the fields." />
          <CardBody className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="FIT sheet">
                <select
                  value={fitSheet}
                  onChange={(e) => onFitSheet(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="">— choose —</option>
                  {sheets.map((s) => (
                    <option key={s.name} value={s.name}>
                      {s.name} ({s.rowCount} rows)
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={`Platform sheet (${platform.toUpperCase()})`}>
                <select
                  value={platformSheet}
                  onChange={(e) => onPlatformSheet(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="">— choose —</option>
                  {sheets.map((s) => (
                    <option key={s.name} value={s.name}>
                      {s.name} ({s.rowCount} rows)
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            {fitSheet && platformSheet && fitSheet === platformSheet && (
              <Banner tone="warn">The FIT and platform sheets must be different.</Banner>
            )}
            <div className="grid gap-4 lg:grid-cols-2">
              <ColumnMapper title="FIT columns" sheet={sheetByName.get(fitSheet)} value={fitMap} onChange={setFitMap} />
              <ColumnMapper
                title={`${platform.toUpperCase()} columns`}
                sheet={sheetByName.get(platformSheet)}
                value={platformMap}
                onChange={setPlatformMap}
              />
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={() => void run()} disabled={!canRun}>
                {busy === 'run' ? <Spinner /> : 'Run Match & Download'}
              </Button>
              <span className="text-xs text-slate-400">ID is required for both sides; Address powers the fuzzy fallback.</span>
            </div>
          </CardBody>
        </Card>
      )}

      {/* 3. Summary */}
      {summary && (
        <Card>
          <CardHeader
            title="Match complete"
            subtitle={`${summary.filename} downloaded.`}
            action={<Badge tone="green">{summary.matchedRows} rows</Badge>}
          />
          <CardBody className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {(Object.keys(summary.counts) as LocationMatchStatus[]).map((s) =>
                summary.counts[s] > 0 ? (
                  <Badge key={s} tone={STATUS_TONE[s]}>
                    {s}: {summary.counts[s]}
                  </Badge>
                ) : null,
              )}
            </div>
            <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              <Stat label="Platform rows" value={summary.totalPlatformRows} />
              <Stat label="After dedup" value={summary.matchedRows} />
              <Stat label="By ID" value={summary.byMethod.ID} />
              <Stat label="By address" value={summary.byMethod.Address} />
              <Stat label="City alerts" value={summary.cityAlerts} />
            </dl>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="mt-0.5 font-semibold text-slate-800">{value}</dd>
    </div>
  );
}
